import { useEffect, useState, useCallback } from "react";
import { KeyRound, ShieldCheck, Loader2, Link2Off, RefreshCw, RotateCw, AlertTriangle, Wallet, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  getGateStatus, connectGate, disconnectGate, getGateBalance, placeGateOrder, reconcileGate, fmtUsd,
} from "@/lib/api";

const PAIRS = ["BTC_USDT", "ETH_USDT", "SOL_USDT", "BNB_USDT", "XRP_USDT"];

export const ApiSettings = ({ onChanged }) => {
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // trade form
  const [pair, setPair] = useState("BTC_USDT");
  const [side, setSide] = useState("buy");
  const [orderType, setOrderType] = useState("market");
  const [amount, setAmount] = useState("");
  const [price, setPrice] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [trading, setTrading] = useState(false);

  const load = useCallback(async () => {
    try { setStatus(await getGateStatus()); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (apiKey.length < 8 || apiSecret.length < 8) { toast.error("Lütfen geçerli API anahtarı ve secret girin."); return; }
    setBusy(true);
    try {
      const res = await connectGate({ api_key: apiKey, api_secret: apiSecret });
      setApiKey(""); setApiSecret("");
      if (res.reachable) toast.success(`Gate.io bağlandı · Bakiye: ${fmtUsd(res.usdt_balance)}`);
      else toast.warning(res.last_error || "Anahtarlar kaydedildi, ancak canlı erişim doğrulanamadı.");
      await load(); onChanged?.();
    } catch (e) {
      toast.error("Bağlantı başarısız: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const refreshBalance = async () => {
    setRefreshing(true);
    try {
      const res = await getGateBalance();
      toast.success(`Canlı bakiye: ${fmtUsd(res.usdt_balance)}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Bakiye alınamadı"); }
    finally { setRefreshing(false); }
  };

  const [syncing, setSyncing] = useState(false);
  const reconcile = async () => {
    setSyncing(true);
    try {
      const res = await reconcileGate();
      toast(res.message);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Eşitleme başarısız"); }
    finally { setSyncing(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await disconnectGate(); toast.success("Gate.io bağlantısı kaldırıldı."); await load(); onChanged?.(); }
    finally { setBusy(false); }
  };

  const doTrade = async () => {
    if (!amount || Number(amount) <= 0) { toast.error("Geçerli bir miktar girin."); return; }
    if (orderType === "limit" && (!price || Number(price) <= 0)) { toast.error("Limit emri için fiyat girin."); return; }
    setTrading(true);
    try {
      const res = await placeGateOrder({
        pair, side, order_type: orderType,
        amount: Number(amount), price: orderType === "limit" ? Number(price) : undefined,
      });
      const o = res.order;
      toast.success(`GERÇEK ${side === "buy" ? "ALIM" : "SATIM"} gönderildi · ${pair} · durum: ${o.status}`);
      setAmount(""); setPrice(""); setConfirming(false);
      await load();
    } catch (e) {
      toast.error("Emir başarısız: " + (e.response?.data?.detail || e.message));
    } finally { setTrading(false); }
  };

  const connected = status?.connected;
  const inp = "mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm h-11";
  const amountLabel = orderType === "market"
    ? (side === "buy" ? "Tutar (USDT harcanacak)" : "Miktar (satılacak coin adedi)")
    : "Miktar (coin adedi)";

  return (
    <div className="max-w-2xl space-y-6" data-testid="api-settings">
      <div className="flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-[var(--accent)]" />
        <h2 className="font-head font-bold text-xl">API Ayarları · Gate.io</h2>
      </div>

      {/* Connection card */}
      <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-5">
        <div>
          <h3 className="font-head font-bold text-sm">Gate.io API Anahtarları (Canlı Spot İşlem)</h3>
          <p className="text-xs text-[var(--text-faint)] mt-1 leading-relaxed">
            Gate.io'da <b className="text-[var(--text-muted)]">APIv4</b> anahtarı oluşturun ve yalnızca
            <b className="text-[var(--text-muted)]"> Spot Trading (Read-Write)</b> iznini açın.
            Güvenliğiniz için <b className="text-[var(--neg)]">Withdraw (para çekme) iznini kapalı</b> tutun.
            Anahtarlarınız veritabanında şifrelenerek saklanır.
          </p>
        </div>

        {connected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-lg bg-[#00FFA3]/10 border border-[#00FFA3]/25 px-4 py-3">
              <ShieldCheck className="h-5 w-5 text-[var(--pos)] flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-bold text-[var(--pos)]">Bağlı</div>
                <div className="text-xs text-[var(--text-dim)] font-mono truncate" data-testid="gate-masked-key">{status.api_key_masked}</div>
              </div>
            </div>

            <div className="rounded-lg border border-[var(--border)] bg-[var(--app-bg)] p-4 flex items-center gap-3">
              <Wallet className="h-5 w-5 text-[var(--accent)]" />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-faint)]">Canlı Gate.io Spot USDT</div>
                <div className="font-head font-black text-xl" data-testid="gate-usdt-balance">
                  {status.reachable && status.usdt_balance != null ? fmtUsd(status.usdt_balance) : "—"}
                </div>
              </div>
              <Button data-testid="refresh-balance" size="sm" variant="outline" onClick={refreshBalance} disabled={refreshing}
                className="ml-auto h-9 border-[var(--border)] hover:bg-[var(--surface-hover)]">
                {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1.5" /> Yenile</>}
              </Button>
            </div>

            {status.balances?.length > 0 && (
              <div className="rounded-lg border border-[var(--border)] bg-[var(--app-bg)] p-4" data-testid="gate-balances">
                <div className="text-[10px] uppercase tracking-wider text-[var(--text-faint)] mb-2">Varlıklar</div>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs font-mono">
                  {status.balances.slice(0, 10).map((b) => (
                    <div key={b.currency} className="flex justify-between">
                      <span className="text-[var(--text-dim)]">{b.currency}</span>
                      <span>{b.available}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {status.last_error && (
              <div className="flex items-start gap-2 rounded-lg bg-[#FFB020]/10 border border-[#FFB020]/25 px-4 py-3" data-testid="gate-warning">
                <AlertTriangle className="h-4 w-4 text-[var(--warn)] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#FFD98A] leading-relaxed">{status.last_error}</p>
              </div>
            )}

            <div className="rounded-lg border border-[var(--border)] bg-[var(--app-bg)] p-4 flex items-center gap-3">
              <RotateCw className="h-5 w-5 text-[var(--accent)] flex-shrink-0" />
              <div className="min-w-0">
                <div data-testid="position-check-title" className="text-sm font-bold text-[var(--text-primary)]">Canlı Pozisyon Kontrolü</div>
                <div data-testid="position-check-description" className="text-[11px] text-[var(--text-faint)] leading-snug">Borsa miktarlarıyla kayıtları karşılaştırır. Maliyet veya bot sahipliği otomatik değiştirilmez.</div>
              </div>
              <Button data-testid="reconcile-gate" size="sm" variant="outline" onClick={reconcile} disabled={syncing}
                className="ml-auto h-9 border-[var(--border)] hover:bg-[var(--surface-hover)]">
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kontrol Et"}
              </Button>
            </div>


            <Button data-testid="disconnect-gate" onClick={disconnect} disabled={busy}
              variant="outline" className="w-full h-11 border-[#FF3366]/40 text-[var(--neg)] hover:bg-[#FF3366]/10 hover:text-[var(--neg)]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2Off className="h-4 w-4 mr-1.5" /> Bağlantıyı Kaldır</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-[var(--text-dim)]">API Key</Label>
              <Input data-testid="gate-api-key" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder="Gate.io API anahtarınız" className={inp + " font-mono"} />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-dim)]">API Secret</Label>
              <Input data-testid="gate-api-secret" type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)}
                placeholder="Secret anahtarınız" className={inp + " font-mono"} />
            </div>
            <Button data-testid="gate-connect" onClick={submit} disabled={busy}
              className="w-full h-12 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Güvenli Bağlan"}
            </Button>
          </div>
        )}
      </div>

      {/* Live manual trade panel */}
      {connected && status.reachable && (
        <div className="rounded-xl border border-[#FF3366]/30 bg-[var(--surface)] p-6 space-y-5" data-testid="gate-trade-panel">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[var(--warn)]" />
            <h3 className="font-head font-bold text-sm">Manuel Canlı İşlem (GERÇEK PARA)</h3>
          </div>
          <p className="text-xs text-[#FFD98A] leading-relaxed rounded-lg bg-[#FFB020]/10 border border-[#FFB020]/25 px-4 py-3">
            ⚠️ Bu panel Gate.io hesabınızda <b>gerçek</b> emir gönderir ve gerçek bakiyenizi kullanır. Küçük tutarlarla başlayın.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 flex gap-2">
              <Button type="button" data-testid="trade-side-buy" onClick={() => setSide("buy")}
                className={`flex-1 h-11 font-bold ${side === "buy" ? "bg-[var(--pos)] text-black hover:bg-[var(--pos-hover)] hover:text-black" : "bg-transparent border border-[var(--border)] text-[var(--text-dim)] hover:bg-[var(--surface-hover)]"}`}>
                <TrendingUp className="h-4 w-4 mr-1.5" /> AL
              </Button>
              <Button type="button" data-testid="trade-side-sell" onClick={() => setSide("sell")}
                className={`flex-1 h-11 font-bold ${side === "sell" ? "bg-[var(--neg)] text-white hover:bg-[var(--neg-hover)] hover:text-white" : "bg-transparent border border-[var(--border)] text-[var(--text-dim)] hover:bg-[var(--surface-hover)]"}`}>
                <TrendingDown className="h-4 w-4 mr-1.5" /> SAT
              </Button>
            </div>

            <div>
              <Label className="text-xs text-[var(--text-dim)]">Parite</Label>
              <select data-testid="trade-pair" value={pair} onChange={(e) => setPair(e.target.value)}
                className={inp + " w-full rounded-md px-3"}>
                {PAIRS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-[var(--text-dim)]">Emir Türü</Label>
              <select data-testid="trade-type" value={orderType} onChange={(e) => setOrderType(e.target.value)}
                className={inp + " w-full rounded-md px-3"}>
                <option value="market">Market</option>
                <option value="limit">Limit</option>
              </select>
            </div>

            <div className={orderType === "limit" ? "" : "col-span-2"}>
              <Label className="text-xs text-[var(--text-dim)]">{amountLabel}</Label>
              <Input data-testid="trade-amount" inputMode="decimal" value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="0.00" className={inp} />
            </div>
            {orderType === "limit" && (
              <div>
                <Label className="text-xs text-[var(--text-dim)]">Fiyat (USDT)</Label>
                <Input data-testid="trade-price" inputMode="decimal" value={price}
                  onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                  placeholder="0.00" className={inp} />
              </div>
            )}
          </div>

          {!confirming ? (
            <Button data-testid="trade-submit" onClick={() => setConfirming(true)}
              className="w-full h-12 bg-[var(--warn)] text-black font-bold hover:opacity-90">
              Emri İncele
            </Button>
          ) : (
            <div className="space-y-2 rounded-lg border border-[#FF3366]/40 bg-[#FF3366]/5 p-4">
              <p className="text-sm text-[var(--text-primary)]">
                <b className={side === "buy" ? "text-[var(--pos)]" : "text-[var(--neg)]"}>
                  {side === "buy" ? "GERÇEK ALIM" : "GERÇEK SATIM"}
                </b>{" "}· {pair} · {orderType === "market" ? "Market" : `Limit @ ${price}`} · Miktar: {amount}
              </p>
              <div className="flex gap-2">
                <Button data-testid="trade-confirm" onClick={doTrade} disabled={trading}
                  className="flex-1 h-11 bg-[var(--neg)] text-white font-bold hover:bg-[var(--neg-hover)] hover:text-white">
                  {trading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Onayla ve Gönder"}
                </Button>
                <Button variant="outline" onClick={() => setConfirming(false)} disabled={trading}
                  className="h-11 border-[var(--border)] hover:bg-[var(--surface-hover)]">Vazgeç</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
